import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  Check,
  CheckCheck,
  Archive,
  Trash2,
  Filter,
  Target,
  MessageSquare,
  Lightbulb,
  Users,
  Clock,
  ExternalLink,
  MoreHorizontal,
  CheckCircle,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { notificationsService } from "@/services/notificationsService";
import { format, isToday, isYesterday, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useWebSocket } from "@/hooks/useWebSocket";

interface Notification {
  id: string;
  type: 'check_in' | 'goal' | 'chat' | 'insight' | 'team' | 'message' | 'nudge' | 'checkin_reminder' | 'team_alert';
  title: string;
  message: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  read: boolean;
  archived?: boolean;
  action_url?: string;
  action_text?: string;
  metadata?: any;
  data?: any; // For compatibility with existing schema
  created_at: string;
  expires_at?: string;
}

interface NotificationDrawerProps {
  trigger?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function NotificationDrawer({ 
  trigger, 
  isOpen, 
  onOpenChange 
}: NotificationDrawerProps) {
  const { user } = useUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const notificationTypes = [
    { value: 'all', label: 'All Types', icon: <Bell className="h-4 w-4" /> },
    { value: 'check_in', label: 'Check-ins', icon: <CheckCircle className="h-4 w-4" /> },
    { value: 'goal', label: 'Goals', icon: <Target className="h-4 w-4" /> },
    { value: 'chat', label: 'Chat', icon: <MessageSquare className="h-4 w-4" /> },
    { value: 'insight', label: 'Insights', icon: <Lightbulb className="h-4 w-4" /> },
    { value: 'team', label: 'Team', icon: <Users className="h-4 w-4" /> },
  ];

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
    }
  }, [user?.id]);

  // Reload notifications when drawer opens to get fresh data
  useEffect(() => {
    if (user?.id && isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [notifications, typeFilter]);

  // WebSocket subscription for real-time notifications
  const { on, off, connected } = useWebSocket();
  
  useEffect(() => {
    if (!connected || !user?.id) return;

    const handleNewNotification = (notification: any) => {
      // Only add unread notifications (since we only show unread ones)
      if (!notification.read) {
        setNotifications((prev) => {
          // Check if notification already exists
          const exists = prev.some((n) => n.id === notification.id);
          if (exists) return prev;
          
          // Process notification to match our format
          const processedNotification: Notification = {
            ...notification,
            id: notification.id,
            type: notification.type,
            title: notification.title,
            message: notification.message,
            priority: notification.priority || 'normal',
            read: notification.read || false,
            archived: notification.archived || false,
            action_url: notification.actionUrl || notification.action_url,
            action_text: notification.actionText || notification.action_text,
            metadata: notification.metadata || notification.data || {},
            created_at: notification.createdAt || notification.created_at,
            expires_at: notification.expiresAt || notification.expires_at,
          };
          
          return [processedNotification, ...prev];
        });
      }
    };

    const cleanup = on('notifications:new', handleNewNotification);

    return () => {
      cleanup();
    };
  }, [connected, user?.id, on, off]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // Load unread notifications via API
      // apiService.get() returns unwrapped data directly (not { success, data } wrapper)
      const notifications = await notificationsService.getNotifications({
        read: false,
        limit: 50,
      });

      // Handle both array response and object with notifications property
      const notificationsList = Array.isArray(notifications) 
        ? notifications 
        : (notifications?.notifications || notifications?.data || []);

      // Ensure all notifications have required fields with defaults
      const processedNotifications = notificationsList.map((notification: any) => ({
        ...notification,
        id: notification.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        priority: notification.priority || 'normal',
        read: notification.read || false,
        archived: notification.archived || false,
        action_url: notification.actionUrl || notification.action_url,
        action_text: notification.actionText || notification.action_text,
        metadata: notification.metadata || notification.data || {},
        created_at: notification.createdAt || notification.created_at,
        expires_at: notification.expiresAt || notification.expires_at,
      }));

      setNotifications(processedNotifications);
    } catch (error) {
      console.error('(Drawer) Error loading notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...notifications];

    // Since all notifications are unread now, no need for read/unread filter
    // Just apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    setFilteredNotifications(filtered);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);

      // Remove the notification from the list since we only show unread ones
      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    if (notifications.length === 0) {
      toast.info('No notifications to delete');
      return;
    }

    try {
      await notificationsService.deleteAll();

      setNotifications([]);
      toast.success(`Deleted ${notifications.length} notifications`);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('Failed to delete all notifications');
    }
  };

  const archiveNotification = async (notificationId: string) => {
    try {
      await notificationsService.archiveNotification(notificationId);

      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );

      toast.success('Notification archived');
    } catch (error) {
      console.error('Error archiving notification:', error);
      toast.error('Failed to archive notification');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationsService.deleteNotification(notificationId);

      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );

      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  const getNotificationIcon = (type: string) => {
    const typeConfig = notificationTypes.find(t => t.value === type);
    return typeConfig?.icon || <Bell className="h-4 w-4" />;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 'normal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
    }
  };

  const getTimeDisplay = (createdAt: string) => {
    const date = new Date(createdAt);
    
    if (isToday(date)) {
      return `Today ${format(date, 'h:mm a')}`;
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const unreadCount = notifications.length; // All loaded notifications are unread

  const defaultTrigger = (
    <Button variant="ghost" size="sm" className="relative">
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <Badge 
          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
          variant="destructive"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}
    </Button>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        {trigger || defaultTrigger}
      </SheetTrigger>
      <SheetContent className="w-full sm:w-[400px] md:w-[540px]">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary">{unreadCount} unread</Badge>
                )}
              </SheetTitle>
              <SheetDescription>
                Stay updated with your progress and team activity
              </SheetDescription>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                {notificationTypes.map((type) => (
                  <DropdownMenuItem 
                    key={type.value} 
                    onClick={() => setTypeFilter(type.value)}
                  >
                    {type.icon}
                    <span className="ml-2">{type.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetHeader>

        {/* Action Bar */}
        <div className="flex items-center gap-2 py-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={markAllAsRead}
            disabled={notifications.length === 0}
            className="h-10 text-sm"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Delete All</span>
            <span className="sm:hidden">Delete</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={loadNotifications} className="h-10 text-sm">
            Refresh
          </Button>
        </div>

        <Separator />

        {/* Notifications List */}
        <ScrollArea className="h-[calc(100vh-200px)] mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center space-y-2">
                <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="text-sm text-muted-foreground">Loading notifications...</p>
              </div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center space-y-2">
                <Bell className="h-8 w-8 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  No notifications
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredNotifications.map((notification) => (
                <Card 
                  key={notification.id} 
                  className={cn(
                    "cursor-pointer transition-colors hover:bg-muted/50",
                    !notification.read && "border-primary/50 bg-primary/5"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "p-2 rounded-full",
                        notification.read 
                          ? "bg-muted text-muted-foreground" 
                          : "bg-primary/10 text-primary"
                      )}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 space-y-1">
                        <div className="flex items-start justify-between">
                          <h4 className={cn(
                            "text-sm font-medium",
                            !notification.read && "font-semibold"
                          )}>
                            {notification.title}
                          </h4>
                          
                          <div className="flex items-center gap-1">
                            {notification.priority !== 'normal' && (
                              <Badge className={cn("text-xs", getPriorityColor(notification.priority))}>
                                {notification.priority}
                              </Badge>
                            )}
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreHorizontal className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!notification.read && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}>
                                    <Check className="h-4 w-4 mr-2" />
                                    Mark as Read
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  archiveNotification(notification.id);
                                }}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        <p className="text-sm text-muted-foreground">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {getTimeDisplay(notification.created_at)}
                          </span>
                          
                          {notification.action_text && notification.action_url && (
                            <Button variant="ghost" size="sm" className="h-auto p-1 text-xs">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {notification.action_text}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}